import type { AnalysisType } from '../../../shared/types';
import { tintSignature } from '../../utils/tints';

/* ── Vocabulaire de formes (mode pastel) ──────────────────────────────
   Là où la charte de référence pose des étoiles, triangles et trapèzes
   en fond, Evinly pose des CONTENANTS À BOIRE : bouteille de vin,
   bouteille de bière, canette, verre à vin, flûte, verre à cocktail,
   pinte, carafe, tonneau. Le décor reste sectoriel.

   Le nuancier compte cinq teintes pour neuf typologies : trois d'entre
   elles servent deux fois. La silhouette lève l'ambiguïté — bouteille de
   vin contre flûte, canette contre cocktail, carafe contre pinte.

   Chaque silhouette est un tracé fermé dans un carré de 100, peint puis
   recontouré d'un trait de sa propre couleur (`stroke-linejoin: round`) :
   les angles s'adoucissent sans détail superflu, et la forme reste lisible
   de la vitrine de connexion (300 px) au porte-icône du bandeau (52 px).

   Elles servent partout où l'icône seule ne suffit pas : bandeau d'analyse,
   écran d'attente, vitrine de l'écran de connexion. */

type Spec = { d: string; stroke: number };

export const SHAPES = {
  /** Bordelaise : épaules franches, col court. */
  bouteilleVin:   { d: 'M41 5 H59 V30 C59 37 67 39 67 48 V89 C67 93 64 95 60 95 H40 C36 95 33 93 33 89 V48 C33 39 41 37 41 30 Z', stroke: 5 },
  /** Longneck : col long, épaules tombantes, capsule. */
  bouteilleBiere: { d: 'M42 5 H58 V11 H56 V36 L65 50 V89 C65 93 62 95 58 95 H42 C38 95 35 93 35 89 V50 L44 36 V11 H42 Z', stroke: 5 },
  /** Canette : fût droit, deux sertis. */
  canette:        { d: 'M37 6 H63 L69 13 V87 L63 94 H37 L31 87 V13 Z', stroke: 5 },
  /** Verre à vin : calice large, jambe, pied. */
  verreVin:       { d: 'M27 6 H73 C73 36 62 50 54 53 V81 H69 V88 H31 V81 H46 V53 C38 50 27 36 27 6 Z', stroke: 5 },
  /** Flûte à champagne : calice étroit et haut. */
  flute:          { d: 'M39 5 H61 V44 C61 56 55 61 53 63 V82 H66 V89 H34 V82 H47 V63 C45 61 39 56 39 44 Z', stroke: 5 },
  /** Verre à cocktail : cône ouvert sur jambe. */
  cocktail:       { d: 'M15 8 H85 L54 48 V82 H70 V89 H30 V82 H46 V48 Z', stroke: 5 },
  /** Pinte : gobelet évasé. */
  pinte:          { d: 'M30 6 H70 L66 90 C66 93 63 95 60 95 H40 C37 95 34 93 34 90 Z', stroke: 5 },
  /** Carafe : col étroit, panse large. */
  carafe:         { d: 'M41 5 H59 V27 C59 38 88 46 88 70 C88 87 74 95 50 95 C26 95 12 87 12 70 C12 46 41 38 41 27 Z', stroke: 5 },
  /** Tonneau : douves bombées. */
  tonneau:        { d: 'M27 12 C27 8 31 6 35 6 H65 C69 6 73 8 73 12 C81 30 81 70 73 88 C73 92 69 94 65 94 H35 C31 94 27 92 27 88 C19 70 19 30 27 12 Z', stroke: 5 },
} satisfies Record<string, Spec>;

export type ShapeName = keyof typeof SHAPES;

/** Un contenant par typologie. Les doublons de couleur sont volontairement
 *  éloignés dans le vocabulaire : deux teintes identiques, deux silhouettes
 *  opposées (bouteille contre flûte, canette contre cocktail). */
const BY_ANALYSIS: Record<AnalysisType, ShapeName> = {
  general_question: 'carafe',
  brand_name: 'bouteilleVin',
  slogan: 'canette',
  concept: 'verreVin',
  label: 'bouteilleBiere',
  campaign: 'flute',
  social_post: 'cocktail',
  event_activation: 'pinte',
  creative_brief: 'tonneau',
};

export function shapeFor(type: AnalysisType | string | null | undefined): ShapeName {
  return (type && BY_ANALYSIS[type as AnalysisType]) || 'verreVin';
}

interface Props {
  /** Typologie d'analyse — détermine silhouette ET couleur. */
  type?: AnalysisType | string | null;
  /** Silhouette imposée, quand elle ne vient pas d'une typologie (décor). */
  shape?: ShapeName;
  /** Couleur imposée (toute valeur CSS). Par défaut : la teinte signature de la typologie. */
  color?: string;
  className?: string;
}

/**
 * Rend la silhouette en `currentColor` si aucune couleur ni typologie n'est
 * donnée — ce qui permet de la piloter depuis le parent (filigrane, survol).
 */
export default function TypologyShape({ type, shape, color, className = 'h-full w-full' }: Props) {
  const spec = SHAPES[shape ?? shapeFor(type)];
  const fill = color ?? (type ? tintSignature(type) : 'currentColor');

  return (
    <svg className={className} viewBox="0 0 100 100" aria-hidden="true" focusable="false">
      <path
        d={spec.d}
        fill={fill}
        stroke={fill}
        strokeWidth={spec.stroke}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
